import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection, $createParagraphNode } from 'lexical';
import {
  $isTableCellNode, $isTableNode, $isTableRowNode,
  $isTableSelection, $getTableCellNodeFromLexicalNode,
  $getTableColumnIndexFromTableCellNode,
  $insertTableRowAtSelection, $insertTableColumnAtSelection,
  $deleteTableRowAtSelection, $deleteTableColumnAtSelection,
  $mergeCells, $unmergeCell, TableCellHeaderStates,
  $createTableRowNode, $createTableCellNode,
} from '@lexical/table';

// ── Deep-clone a Lexical node tree (for duplicate row) ───────────────────────

function deepCloneNode(editor, node) {
  const entry     = editor._nodes.get(node.getType());
  const NodeClass = entry?.klass ?? entry?.replace;
  if (!NodeClass) return null;
  let cloned;
  try {
    cloned = NodeClass.importJSON(node.exportJSON());
  } catch (err) {
    return null;
  }
  if (typeof node.getChildren === 'function') {
    const children = node.getChildren();
    for (let i = 0; i < children.length; i++) {
      const c = deepCloneNode(editor, children[i]);
      if (c) cloned.append(c);
    }
  }
  return cloned;
}

// ── Swatch data ───────────────────────────────────────────────────────────────

const BG_SWATCHES = [
  { label: 'Clear',   value: null       },
  { label: 'Yellow',  value: '#fff9c4'  },
  { label: 'Green',   value: '#c8e6c9'  },
  { label: 'Blue',    value: '#bbdefb'  },
  { label: 'Pink',    value: '#f8bbd0'  },
  { label: 'Purple',  value: '#e1bee7'  },
  { label: 'Orange',  value: '#ffccbc'  },
  { label: 'Teal',    value: '#b2dfdb'  },
  { label: 'Gray',    value: '#eeeeee'  },
  { label: 'White',   value: '#ffffff'  },
];

const BORDER_SWATCHES = [
  { label: 'Default',     value: null,          special: 'auto'  },
  { label: 'No border',   value: 'none',        special: 'none'  },
  { label: 'Transparent', value: 'transparent', special: 'trans' },
  { label: 'Black',       value: '#000000'     },
  { label: 'Dark gray',   value: '#555555'     },
  { label: 'Gray',        value: '#999999'     },
  { label: 'Silver',      value: '#cccccc'     },
  { label: 'Red',         value: '#e03131'     },
  { label: 'Orange',      value: '#e8590c'     },
  { label: 'Green',       value: '#2f9e44'     },
  { label: 'Blue',        value: '#1971c2'     },
  { label: 'Purple',      value: '#7048e8'     },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getTableCellFromSelection(sel) {
  if (!sel) return null;
  if ($isTableSelection(sel)) {
    for (const n of sel.getNodes()) { if ($isTableCellNode(n)) return n; }
    return null;
  }
  if ($isRangeSelection(sel)) return $getTableCellNodeFromLexicalNode(sel.anchor.getNode());
  return null;
}

// ── Plugin ────────────────────────────────────────────────────────────────────

export default function TableContextMenuPlugin() {
  const [editor] = useLexicalComposerContext();
  const [menu, setMenu] = useState(null);
  const menuRef = useRef(null);
  const closeMenu = useCallback(() => setMenu(null), []);

  useEffect(() => {
    const root = editor.getRootElement();
    if (!root) return;
    const onContextMenu = (e) => {
      let el = e.target;
      while (el && el !== root) {
        if (el.tagName === 'TD' || el.tagName === 'TH') {
          e.preventDefault();
          let hasTableSel = false, isMergedCell = false,
              isRowHeader = false, isColHeader = false,
              currentBg = null, currentVA = 'middle', currentBorderColor = null;
          editor.read(() => {
            const sel = $getSelection();
            hasTableSel = $isTableSelection(sel);
            const cell = getTableCellFromSelection(sel);
            if (cell) {
              isMergedCell       = cell.getColSpan() > 1 || cell.getRowSpan() > 1;
              isRowHeader        = cell.hasHeaderState(TableCellHeaderStates.ROW);
              isColHeader        = cell.hasHeaderState(TableCellHeaderStates.COLUMN);
              currentBg          = cell.getBackgroundColor();
              currentVA          = cell.getVerticalAlign() || 'middle';
              currentBorderColor = typeof cell.getBorderColor === 'function' ? cell.getBorderColor() : null;
            }
          });
          // Menu is max 430px tall — clamp so it stays on screen
          const x = Math.min(e.clientX, window.innerWidth  - 252);
          const y = Math.min(e.clientY, window.innerHeight - 440);
          setMenu({ x, y, hasTableSel, isMergedCell, isRowHeader, isColHeader, currentBg, currentVA, currentBorderColor });
          return;
        }
        el = el.parentElement;
      }
    };
    root.addEventListener('contextmenu', onContextMenu);
    return () => root.removeEventListener('contextmenu', onContextMenu);
  }, [editor]);

  useEffect(() => {
    if (!menu) return;
    const onKey  = (e) => { if (e.key === 'Escape') closeMenu(); };
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) closeMenu(); };
    document.addEventListener('keydown',   onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown',   onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [menu, closeMenu]);

  const run = useCallback((fn) => { editor.update(fn); closeMenu(); }, [editor, closeMenu]);

  if (!menu) return null;
  const { x, y, hasTableSel, isMergedCell, isRowHeader, isColHeader, currentBg, currentVA, currentBorderColor } = menu;

  // ── inline apply helpers (run inside editor.update) ──────────────────────
  const applyBg = (value) => {
    const sel = $getSelection();
    // null = "Clear": must set explicit transparent so it overrides the CSS rule
    // `th { background: var(--mantine-color-default) }` on header cells.
    const effective = value === null ? 'transparent' : value;
    const fn = (cell) => cell.setBackgroundColor(effective);
    if ($isTableSelection(sel)) sel.getNodes().filter($isTableCellNode).forEach(fn);
    else if ($isRangeSelection(sel)) { const c = $getTableCellNodeFromLexicalNode(sel.anchor.getNode()); if (c) fn(c); }
  };

  const applyVA = (value) => {
    const sel = $getSelection();
    const fn = (cell) => cell.setVerticalAlign(value);
    if ($isTableSelection(sel)) sel.getNodes().filter($isTableCellNode).forEach(fn);
    else if ($isRangeSelection(sel)) { const c = $getTableCellNodeFromLexicalNode(sel.anchor.getNode()); if (c) fn(c); }
  };

  const applyBorder = (value) => {
    const sel = $getSelection();
    const fn = (cell) => { if (typeof cell.setBorderColor === 'function') cell.setBorderColor(value); };
    if ($isTableSelection(sel)) sel.getNodes().filter($isTableCellNode).forEach(fn);
    else if ($isRangeSelection(sel)) { const c = $getTableCellNodeFromLexicalNode(sel.anchor.getNode()); if (c) fn(c); }
  };

  return createPortal(
    <div
      ref={menuRef}
      onMouseDown={(e) => e.preventDefault()}
      style={{
        position: 'fixed', left: x, top: y,
        zIndex: 99999,
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.09)',
        borderRadius: 10,
        boxShadow: '0 8px 30px rgba(0,0,0,0.13), 0 2px 8px rgba(0,0,0,0.06)',
        width: 248,
        maxHeight: 420,
        overflowY: 'auto',
        overflowX: 'hidden',
        fontSize: 12.5,
        userSelect: 'none',
        scrollbarWidth: 'thin',
        scrollbarColor: '#e0e0e0 transparent',
      }}
    >

      {/* ═══ COLORS + ALIGN (pinned at top) ════════════════════════ */}
      <div style={{ padding: '10px 10px 2px', borderBottom: '1px solid #f0f0f0' }}>

        {/* Cell Background */}
        <ColorRow label="Cell BG">
          {BG_SWATCHES.map(({ label, value }) => (
            <Swatch
              key={label} title={label}
              active={
                value === null
                  ? (currentBg === null || currentBg === 'transparent')
                  : (currentBg ?? null) === value
              }
              onClick={() => run(() => applyBg(value))}
              style={{
                background: value === null ? '#f4f4f4' : value,
                border: value === null
                  ? '1.5px dashed #bbb'
                  : '1.5px solid rgba(0,0,0,0.13)',
              }}
            >
              {value === null && <span style={{ fontSize: 10, color: '#999', lineHeight: 1 }}>✕</span>}
            </Swatch>
          ))}
        </ColorRow>

        {/* Border Color */}
        <ColorRow label="Border">
          {BORDER_SWATCHES.map(({ label, value, special }) => (
            <Swatch
              key={label} title={label}
              active={(currentBorderColor ?? null) === value}
              onClick={() => run(() => applyBorder(value))}
              style={
                special === 'auto'  ? { background: '#f4f4f4', border: '1.5px dashed #bbb' } :
                special === 'none'  ? { background: '#fff3f3', border: '1.5px solid #fcc' } :
                special === 'trans' ? {
                  background: 'repeating-linear-gradient(45deg,#ddd 0,#ddd 2px,#fff 2px,#fff 6px)',
                  border: '1.5px solid #ddd',
                } : {
                  background: value,
                  border: `1.5px solid ${value}`,
                }
              }
            >
              {special === 'auto'  && <span style={{ fontSize: 9, color: '#888', fontWeight: 700, letterSpacing: '-0.02em' }}>Auto</span>}
              {special === 'none'  && <span style={{ fontSize: 9, color: '#c00', fontWeight: 700 }}>✕</span>}
            </Swatch>
          ))}
        </ColorRow>

        {/* Vertical Align */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0 7px' }}>
          <span style={labelStyle}>V-Align</span>
          <div style={{ display: 'flex', gap: 3, flex: 1 }}>
            {[['Top','top'],['Mid','middle'],['Bot','bottom']].map(([short, val]) => (
              <button
                key={val}
                onClick={() => run(() => applyVA(val))}
                style={{
                  flex: 1, padding: '3px 0', fontSize: 10.5, fontWeight: currentVA === val ? 700 : 400,
                  border: `1.5px solid ${currentVA === val ? '#1971c2' : '#ddd'}`,
                  borderRadius: 5, cursor: 'pointer',
                  background: currentVA === val ? '#dbeafe' : '#fafafa',
                  color: currentVA === val ? '#1d4ed8' : '#555',
                  transition: 'all 0.12s',
                }}
              >{short}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ TABLE ACTIONS ══════════════════════════════════════════ */}
      <div style={{ padding: '4px 0' }}>

        {/* Insert */}
        <GroupLabel>Insert</GroupLabel>
        <Item onClick={() => run(() => $insertTableRowAtSelection(false))}>↑  Row Above</Item>
        <Item onClick={() => run(() => $insertTableRowAtSelection(true))} >↓  Row Below</Item>
        <Item onClick={() => run(() => {
          const sel  = $getSelection();
          const cell = getTableCellFromSelection(sel);
          if (!cell) return;
          const row = cell.getParent();
          if (!$isTableRowNode(row)) return;

          const newRow = $createTableRowNode();
          if (row.getHeight()) newRow.setHeight(row.getHeight());

          row.getChildren().forEach(origCell => {
            if (!$isTableCellNode(origCell)) return;
            const newCell = $createTableCellNode(origCell.getHeaderStyles());
            const w = origCell.getWidth(); if (w) newCell.setWidth(w);
            const cs = origCell.getColSpan(); if (cs > 1) newCell.setColSpan(cs);
            const rs = origCell.getRowSpan(); if (rs > 1) newCell.setRowSpan(rs);
            const bg = origCell.getBackgroundColor(); if (bg) newCell.setBackgroundColor(bg);
            const va = origCell.getVerticalAlign(); if (va) newCell.setVerticalAlign(va);
            if (typeof origCell.getBorderColor === 'function') {
              const bc = origCell.getBorderColor();
              if (bc != null && typeof newCell.setBorderColor === 'function') newCell.setBorderColor(bc);
            }
            origCell.getChildren().forEach(child => {
              const c = deepCloneNode(editor, child); if (c) newCell.append(c);
            });
            newRow.append(newCell);
          });

          row.insertAfter(newRow);
        })}>⧉  Duplicate Row</Item>
        <Item onClick={() => run(() => $insertTableColumnAtSelection(false))}>←  Column Left</Item>
        <Item onClick={() => run(() => $insertTableColumnAtSelection(true))} >→  Column Right</Item>

        {/* Delete */}
        <Sep />
        <GroupLabel>Delete</GroupLabel>
        <Item danger onClick={() => run(() => {
          const sel = $getSelection();
          const clearCell = (cell) => {
            cell.getChildren().forEach(child => child.remove());
            cell.append($createParagraphNode());
          };
          if ($isTableSelection(sel)) sel.getNodes().filter($isTableCellNode).forEach(clearCell);
          else if ($isRangeSelection(sel)) { const c = $getTableCellNodeFromLexicalNode(sel.anchor.getNode()); if (c) clearCell(c); }
        })}>Clear Cell Contents</Item>
        {hasTableSel && (
          <Item danger onClick={() => run(() => {
            const sel = $getSelection();
            if (!$isTableSelection(sel)) return;
            const cells = sel.getNodes().filter($isTableCellNode);
            const rows = [...new Map(cells.map(c => [c.getParent().getKey(), c.getParent()])).values()]
              .filter($isTableRowNode);
            const table = rows[0]?.getParent();
            if (!$isTableNode(table)) return;
            const allRows = table.getChildren().filter($isTableRowNode);
            if (rows.length >= allRows.length) { table.remove(); return; }
            rows.forEach(r => r.remove());
          })}>Delete Selected Cells</Item>
        )}
        <Item danger onClick={() => run(() => $deleteTableRowAtSelection())}>Row</Item>
        <Item danger onClick={() => run(() => $deleteTableColumnAtSelection())}>Column</Item>
        {hasTableSel && <>
          <Item danger onClick={() => run(() => {
            const sel = $getSelection();
            if (!$isTableSelection(sel)) return;
            const cells = sel.getNodes().filter($isTableCellNode);
            const rows = [...new Map(cells.map(c => [c.getParent().getKey(), c.getParent()])).values()]
              .filter($isTableRowNode);
            const table = rows[0]?.getParent();
            if (!$isTableNode(table)) return;
            const allRows = table.getChildren().filter($isTableRowNode);
            if (rows.length >= allRows.length) { table.remove(); return; }
            rows.forEach(r => r.remove());
          })}>Selected Rows</Item>
          <Item danger onClick={() => run(() => {
            const sel = $getSelection();
            if (!$isTableSelection(sel)) return;
            const cells = sel.getNodes().filter($isTableCellNode);
            const colIndices = [...new Set(cells.map(c => $getTableColumnIndexFromTableCellNode(c)))].sort((a, b) => b - a);
            const table = cells[0]?.getParent()?.getParent();
            if (!$isTableNode(table)) return;
            const tableRows = table.getChildren().filter($isTableRowNode);
            const totalCols = tableRows[0]?.getChildren().length ?? 0;
            if (colIndices.length >= totalCols) { table.remove(); return; }
            for (const idx of colIndices) {
              for (const row of tableRows) {
                const cols = row.getChildren();
                if (idx < cols.length && $isTableCellNode(cols[idx])) cols[idx].remove();
              }
            }
          })}>Selected Columns</Item>
        </>}
        <Item danger onClick={() => run(() => {
          const sel = $getSelection();
          const cell = getTableCellFromSelection(sel);
          if (!cell) return;
          const table = cell.getParent()?.getParent();
          if ($isTableNode(table)) table.remove();
        })}>Table</Item>

        {/* Merge / Unmerge */}
        {(hasTableSel || isMergedCell) && <>
          <Sep />
          <GroupLabel>Cells</GroupLabel>
          {hasTableSel && (
            <Item onClick={() => run(() => {
              const sel = $getSelection();
              if ($isTableSelection(sel)) {
                const cells = sel.getNodes().filter($isTableCellNode);
                if (cells.length > 1) $mergeCells(cells);
              }
            })}>Merge Cells</Item>
          )}
          {isMergedCell && <Item onClick={() => run(() => $unmergeCell())}>Unmerge Cell</Item>}
        </>}

        {/* Header */}
        <Sep />
        <GroupLabel>Header</GroupLabel>
        <Item
          check={isRowHeader}
          onClick={() => run(() => {
            const sel = $getSelection();
            const cell = getTableCellFromSelection(sel);
            if (!cell) return;
            const row = cell.getParent();
            if (!$isTableRowNode(row)) return;
            const ns = isRowHeader ? TableCellHeaderStates.NO_STATUS : TableCellHeaderStates.ROW;
            row.getChildren().forEach(c => { if ($isTableCellNode(c)) c.setHeaderStyles(ns, TableCellHeaderStates.ROW); });
          })}
        >Row Header</Item>
        <Item
          check={isColHeader}
          onClick={() => run(() => {
            const sel = $getSelection();
            const cell = getTableCellFromSelection(sel);
            if (!cell) return;
            const row = cell.getParent(); if (!$isTableRowNode(row)) return;
            const table = row.getParent(); if (!$isTableNode(table)) return;
            const colIdx = $getTableColumnIndexFromTableCellNode(cell);
            const ns = isColHeader ? TableCellHeaderStates.NO_STATUS : TableCellHeaderStates.COLUMN;
            table.getChildren().forEach(r => {
              if (!$isTableRowNode(r)) return;
              const cols = r.getChildren();
              if (colIdx < cols.length && $isTableCellNode(cols[colIdx]))
                cols[colIdx].setHeaderStyles(ns, TableCellHeaderStates.COLUMN);
            });
          })}
        >Column Header</Item>

      </div>
    </div>,
    document.body
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const labelStyle = {
  fontSize: 9.5, fontWeight: 700, color: '#bbb',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  minWidth: 40, flexShrink: 0,
};

// ── Sub-components ────────────────────────────────────────────────────────────

function ColorRow({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 6 }}>
      <span style={{ ...labelStyle, paddingTop: 3 }}>{label}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, flex: 1 }}>
        {children}
      </div>
    </div>
  );
}

function Swatch({ children, title, active, onClick, style }) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        width: 20, height: 20, borderRadius: 4, cursor: 'pointer', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: active ? '0 0 0 2px #1971c2' : 'none',
        outline: active ? '2px solid #fff' : 'none',
        outlineOffset: -3,
        transition: 'box-shadow 0.1s',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function GroupLabel({ children }) {
  return (
    <div style={{
      fontSize: 9.5, fontWeight: 700, color: '#ccc',
      padding: '3px 12px 1px',
      textTransform: 'uppercase', letterSpacing: '0.07em',
    }}>
      {children}
    </div>
  );
}

function Item({ children, onClick, danger, check }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        width: '100%', textAlign: 'left',
        padding: '4.5px 12px', border: 'none',
        background: hover ? (danger ? '#fff5f5' : '#f7f7f7') : 'transparent',
        cursor: 'pointer', fontSize: 12.5,
        color: danger ? '#c0392b' : '#222',
        transition: 'background 0.08s',
      }}
    >
      {check !== undefined && (
        <span style={{ width: 12, fontSize: 11, color: check ? '#2563eb' : 'transparent', flexShrink: 0 }}>✓</span>
      )}
      {children}
    </button>
  );
}

function Sep() {
  return <div style={{ height: 1, background: '#f2f2f2', margin: '3px 0' }} />;
}
